/* eslint-disable @typescript-eslint/indent */
import MiniSearch, { Query, SearchResult } from 'minisearch';
import Reservation from '@/model/Reservation';

export enum SearchType {
  None,
  DayReservations,
  AllReservations,
  ContactName,
  ContactPhone,
  ContactEmail,
  /** Contact fields: name, phone, email (local + API contact search). */
  ContactAll,
}

interface SearchReservation
{
  id: string;
  name?: string;
  phone?: string;
  email?: string;
  notes?: string;
  contactNotes?: string;
}

type ReservationField = 'name' | 'phone' | 'email' | 'notes' | 'contactNotes';

const ReservationFields: ReservationField[] = ['name', 'phone', 'email', 'notes', 'contactNotes'];

/**
 * Tokenizer matching `elasticlunr.tokenizer`'s default behavior: trim +
 * lowercase + split on whitespace / hyphens. Empty / null / undefined input
 * yields no tokens. No stemming or stop-word filtering — the previous
 * elasticlunr setup relied on a two-pass `$$`-sentinel pipeline whose second
 * pass never actually contributed matches (the merge kept only pass-1 hits),
 * so skipping the stemmer entirely is behavior-preserving.
 */
const TOKEN_SEPARATOR = /[\s-]+/;

function tokenize(text: string | null | undefined): string[] {
  if (text === null || text === undefined) return [];
  return String(text).trim().toLowerCase().split(TOKEN_SEPARATOR)
    .filter((t) => t !== '');
}

function toSearchReservation(r: Reservation): SearchReservation {
  return {
    id: String(r.id),
    name: r.contact.name ?? undefined,
    phone: r.contact.phone ?? undefined,
    email: r.contact.email ?? undefined,
    notes: r.notes ?? undefined,
    contactNotes: r.contact.notes ?? undefined,
  };
}

function searchCreate(): MiniSearch<SearchReservation> {
  return new MiniSearch<SearchReservation>({
    idField: 'id',
    fields: ReservationFields as unknown as string[],
    tokenize: (text: string) => tokenize(text),
    processTerm: (term: string) => term,
    searchOptions: {
      prefix: true,
      combineWith: 'AND',
    },
  });
}

let rindex = searchCreate();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function searchUpdateReservations(reservations: Reservation[], reservationsById: { [id: number]: Reservation }) {
  reservations.forEach((r) => {
    const idStr = String(r.id);

    if (r.isDeleted) {
      if (rindex.has(idStr)) rindex.discard(idStr);
      return;
    }
    const rdoc = toSearchReservation(r);
    if (rindex.has(rdoc.id)) rindex.replace(rdoc);
    else rindex.add(rdoc);
  });
}

export function searchReset() {
  rindex = searchCreate();
}

/**
 * Build a MiniSearch query that emulates elasticlunr's per-field AND
 * combined with OR across fields (each token must appear in the same field,
 * any field counts). Only fields with boost > 0 participate. Returns `null`
 * when no field should be searched.
 */
function buildPerFieldQuery(
  query: string,
  boosts: { [K in ReservationField]: number },
): Query | null {
  const active = ReservationFields.filter((f) => boosts[f] > 0);
  if (active.length === 0) return null;
  const subqueries: Query[] = active.map((field) => ({
    combineWith: 'AND',
    queries: [query],
    fields: [field],
    prefix: true,
    boost: { [field]: boosts[field] },
  } as unknown as Query));
  return {
    combineWith: 'OR',
    queries: subqueries,
  } as unknown as Query;
}

export function searchForReservations(
  query: string,
  type: SearchType,
  reservationsById: { [id: number]: Reservation },
) {
  if (query.trim().length < 3) return [];

  const boosts: { [K in ReservationField]: number } = {
    name: 0,
    phone: 0,
    email: 0,
    notes: 0,
    contactNotes: 0,
  };

  switch (type) {
  case SearchType.ContactName: boosts.name = 1; break;
  case SearchType.ContactPhone: boosts.phone = 1; break;
  case SearchType.ContactEmail: boosts.email = 1; break;
  case SearchType.ContactAll:
    boosts.name = 1;
    boosts.phone = 1;
    boosts.email = 1;
    break;
  case SearchType.AllReservations:
  case SearchType.DayReservations:
    boosts.name = 1;
    boosts.phone = 1;
    boosts.email = 1;
    boosts.notes = 1;
    boosts.contactNotes = 1;
    break;
  default:
    break;
  }

  const q = buildPerFieldQuery(query, boosts);
  const res: SearchResult[] = q ? rindex.search(q) : [];
  console.log('searchForReservations: ', query, res);
  const reservations = res
    .map((r) => reservationsById[Number(r.id)]);

  return reservations;
}
